/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Tariffs And Services Side Menu Level Component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.10.0
 * @since 1.4.0
 */

import { Fragment, useCallback, useEffect, useState } from 'react';

import {
    Accordion,
    AccordionItem,
    AccordionItemButton,
    AccordionItemHeading,
    AccordionItemPanel,
} from 'react-accessible-accordion';

import { icons } from '../../../assets';
import { ImgStyled } from '../../../components/common/Block/Img';
import { IProductsCategory } from '../tariffsAndServicesTypes';
import { AccordionItemStyled } from './styled/TariffsAndServicesSideMenuStyled';

export interface ITariffsAndServicesSideMenuLevelProps {
    categoryList: IProductsCategory[];
    disabled?: boolean;
    isParentSelected: (id: number) => boolean;
    isSelected: (id: number) => boolean;
    level: number;
    onClick: (id: number) => void;
    parent: number;
    preExpandedCategories: string[];
    title?: string;
}

export const ProductsSideMenuLevel = (props: ITariffsAndServicesSideMenuLevelProps) => {
    const [items, setItems] = useState<IProductsCategory[]>([]);
    const [isActive, setIsActive] = useState(false);
    const [isParentActive, setIsParentActive] = useState(false);

    useEffect(() => {
        if (props.categoryList) {
            setItems(props.categoryList.filter(item => item.parent === props.parent));
        }
    }, [props.categoryList])

    useEffect(() => {
        setIsActive(props.isSelected(props.parent));
        setIsParentActive(props.isParentSelected(props.parent));
    }, [props.isSelected, props.isParentSelected, props.parent])

    const onClickHandler = useCallback(() => {
        props.onClick(props.parent);
    }, [props.onClick, props.parent]);

    return <Fragment>
        <Accordion allowMultipleExpanded={true} allowZeroExpanded={true} preExpanded={props.preExpandedCategories}>
            <AccordionItem dangerouslySetExpanded={props.title ? undefined : true} uuid={props.parent.toString()}>
                {
                    props.title &&
                    <AccordionItemHeading>
                        <AccordionItemButton>
                            <AccordionItemStyled
                                disabled={!!props.disabled}
                                isActive={isActive}
                                isParentActive={isParentActive}
                                level={props.level}
                                onClick={onClickHandler}
                            >
                                <span>{props.title}</span>
                                {
                                    items.length > 0 &&
                                    <ImgStyled
                                        className="small"
                                        src={(isParentActive && props.level === 1)
                                            ? icons.arrowDownWhite : icons.arrowDown}/>
                                }
                            </AccordionItemStyled>
                        </AccordionItemButton>
                    </AccordionItemHeading>
                }
                <AccordionItemPanel>
                    {
                        items.length > 0 &&
                        items.map((subItem) => (
                            <Fragment key={subItem.id}>
                                <ProductsSideMenuLevel
                                    categoryList={props.categoryList}
                                    disabled={subItem.disabled}
                                    isParentSelected={props.isParentSelected}
                                    isSelected={props.isSelected}
                                    level={props.level + 1}
                                    parent={subItem.id}
                                    preExpandedCategories={props.preExpandedCategories}
                                    title={subItem.title}
                                    onClick={props.onClick}
                                />
                            </Fragment>))
                    }
                </AccordionItemPanel>
            </AccordionItem>
        </Accordion>
    </Fragment>
}

export default ProductsSideMenuLevel;
